import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { neighborsLogoPath } from '../../../utils/logoPaths';
import { normalizeNeighborsPath } from '../../layout/neighborsInteriorPaths';
import { useNeighborsNav } from '../../contexts/NeighborsNavContext';
import UserContext from '../../../contexts/UserContext';
import useNeighborsPaymentAccess from '../../hooks/useNeighborsPaymentAccess';
import { canGuestReachPath } from '../../utils/guestAccessRouting';
import TruncatedText from '../TruncatedText';
import {
    CalendarIcon,
    CloseIcon,
    DocumentsIcon,
    GreetingsIcon,
    HomeIcon,
    LinksIcon,
    MenuIcon,
    OrderIcon,
    PaymentIcon,
    ProfileIcon,
    SocialIcon,
    SurveyIcon,
    IconComponent,
} from '../icons';
import './style.less';

type NavItem = {
    path: string;
    label: string;
    Icon: IconComponent;
};

const ALL_NAV_ITEMS: NavItem[] = [
    { path: '/home', label: 'Home', Icon: HomeIcon },
    { path: '/calendar', label: 'Calendar', Icon: CalendarIcon },
    { path: '/menu', label: 'Menu', Icon: MenuIcon },
    { path: '/resident-social', label: 'Social', Icon: SocialIcon },
    { path: '/documents', label: 'Documents', Icon: DocumentsIcon },
    { path: '/links', label: 'Links', Icon: LinksIcon },
    { path: '/greetings', label: 'Greetings', Icon: GreetingsIcon },
    { path: '/order-status', label: 'Order Status', Icon: OrderIcon },
    { path: '/payments', label: 'Payments', Icon: PaymentIcon },
    { path: '/survey', label: 'Survey', Icon: SurveyIcon },
    { path: '/manage-profiles', label: 'Profile', Icon: ProfileIcon },
];

// Mirrors the guestAllowed <PrivateRoute> paths in App.tsx - a guest never sees a nav
// entry for a route they'd be redirected away from.
const GUEST_ALLOWED_NAV_PATHS = new Set<string>([
    '/home',
    '/calendar',
    '/menu',
    '/order-status',
    '/payments',
    '/manage-profiles',
]);

/* Rail widths: 230px on large monitors, stepped down so the rail keeps working
   on laptops and tablet portrait instead of squeezing the content column. */
const RAIL_WIDTH = 'md:w-nbp-184 lg:w-nbp-210 xl:w-nbp-230';

// Must match the drawer's `duration-200` transition below.
const DRAWER_TRANSITION_MS = 200;

/* `hover:text-white` / `focus:text-white` are load-bearing: Semantic's bare
   `a:hover` rule outranks a plain `.text-white`, so without them the links would
   turn Semantic blue on hover. */
const NAV_LINK_BASE =
    'neighbors-sidenav-btn group box-border flex w-full items-center gap-nbp-12 rounded-nbp-12 px-nbp-16 py-nbp-12 text-left text-nbp-14 font-nb-body font-medium no-underline transition-all duration-150 hover:text-white focus:text-white focus:outline-none';

const NAV_LINK_IDLE = 'text-nb-white-75 hover:bg-nb-white-10 focus:bg-nb-white-10';

const NAV_LINK_ACTIVE = 'bg-nb-white-16 text-white font-semibold';

const SidenavLogo = () => (
    <img
        src={`${process.env.PUBLIC_URL}${neighborsLogoPath}`}
        alt="Speak2 Neighbors"
        className="neighbors-sidenav-logo-img block h-auto w-nbp-120 max-w-full object-contain"
    />
);

const SidenavLinks = ({
    items,
    currentPath,
    onNavigate,
}: {
    items: NavItem[];
    currentPath: string;
    onNavigate?: () => void;
}) => (
    <nav className="flex min-w-0 flex-1 flex-col gap-nbp-3 px-nbp-12 py-nbp-20">
        {items.map((item) => {
            const isActive = currentPath === item.path || (item.path === '/home' && currentPath === '/');
            return (
                <Link
                    key={item.path}
                    to={item.path}
                    onClick={onNavigate}
                    aria-current={isActive ? 'page' : undefined}
                    className={`${NAV_LINK_BASE} ${isActive ? NAV_LINK_ACTIVE : NAV_LINK_IDLE}`}
                >
                    <item.Icon
                        size={18}
                        className={`flex-shrink-0 ${isActive ? 'opacity-100' : 'opacity-85 group-hover:opacity-100'}`}
                    />
                    <TruncatedText as="span">{item.label}</TruncatedText>
                </Link>
            );
        })}
    </nav>
);

const NeighborsSidenav = () => {
    const location = useLocation();
    const currentPath = normalizeNeighborsPath(location.pathname);
    const { isMobileNavOpen, closeMobileNav } = useNeighborsNav();
    const { userObject } = useContext(UserContext);
    const { resolvedShowPaymentsEntry, facilityPaymentsEnabled, facilityLookupFailed, loading } = useNeighborsPaymentAccess();

    const isGuest = userObject?.userType === 'guest';
    const navItems = ALL_NAV_ITEMS.filter((item) => {
        if (item.path === '/payments' && !resolvedShowPaymentsEntry) return false;
        // Daily Greetings is sold through card payments: no entry unless the facility has them on
        // (hidden until that is confirmed, so it never flashes in and out). If the lookup fails the entry
        // stays, because the Greetings page shows a retry rather than guessing.
        if (item.path === '/greetings' && !(!loading && (facilityPaymentsEnabled || facilityLookupFailed))) return false;
        if (isGuest && !GUEST_ALLOWED_NAV_PATHS.has(item.path)) return false;
        // Hide entries that would only bounce a waiting guest to /guest/pending.
        if (isGuest && !canGuestReachPath(item.path, userObject?.guestAccess)) return false;
        return true;
    });

    const drawerRef = useRef<HTMLElement | null>(null);
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);

    // `invisible` (visibility: hidden) is what takes the drawer out of the tab
    // order once closed, but setting it in the same render as the close would
    // cut the slide-out transition short. Keep it "visible" until the transition
    // below has actually finished playing.
    const [isDrawerVisible, setIsDrawerVisible] = useState(isMobileNavOpen);

    useEffect(() => {
        if (isMobileNavOpen) {
            setIsDrawerVisible(true);
            return;
        }
        const timeout = window.setTimeout(() => setIsDrawerVisible(false), DRAWER_TRANSITION_MS);
        return () => window.clearTimeout(timeout);
    }, [isMobileNavOpen]);

    // A route change means the drawer has done its job.
    useEffect(() => {
        closeMobileNav();
    }, [location.pathname, closeMobileNav]);

    // The rail replaces the drawer at md and up. If the viewport crosses that
    // boundary while the drawer is open (resize, or a phone rotated to
    // landscape), the drawer and body-scroll lock below would otherwise stay
    // stuck on even though the drawer itself is no longer visible.
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) closeMobileNav();
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [closeMobileNav]);

    // `isMobileNavOpen` lives in a provider above this component, so it survives
    // this component unmounting (a manager logging out, or a route where
    // `showNeighborsAppShell` goes false) and would otherwise reopen the drawer —
    // scroll lock included — the next time it mounts.
    useEffect(() => () => closeMobileNav(), [closeMobileNav]);

    // While the drawer covers the screen: Escape closes it, Tab stays trapped
    // inside it, and the page behind it must not scroll.
    useEffect(() => {
        if (!isMobileNavOpen) return;

        previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
        closeButtonRef.current?.focus();

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' || event.key === 'Esc') {
                closeMobileNav();
                return;
            }

            if (event.key !== 'Tab' || !drawerRef.current) return;

            const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
            );
            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        const previousOverflow = document.body.style.overflow;
        const shell = document.querySelector('.neighbors-interior-shell');
        shell?.setAttribute('inert', '');

        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
            shell?.removeAttribute('inert');
            previouslyFocusedRef.current?.focus();
        };
    }, [isMobileNavOpen, closeMobileNav]);

    return (
        <>
            {/* ── Tablet → monitor: sticky left rail ── */}
            <aside
                className={`neighbors-sidenav z-nb-rail hidden bg-nb-navy text-white shadow-nb-rail md:sticky md:top-0 md:flex md:h-screen md:flex-shrink-0 md:flex-col md:self-start md:overflow-y-auto ${RAIL_WIDTH}`}
                aria-label="Main navigation"
            >
                <Link
                    to="/home"
                    className="neighbors-sidenav-logo-link flex flex-shrink-0 items-center justify-center border-0 border-b border-solid border-nb-white-12 px-nbp-20 pb-nbp-22 pt-nbp-28"
                    aria-label="Speak2 Neighbors home"
                >
                    <SidenavLogo />
                </Link>
                <SidenavLinks items={navItems} currentPath={currentPath} />
            </aside>

            {/* ── Phone: scrim ── */}
            <div
                onClick={closeMobileNav}
                aria-hidden="true"
                className={`fixed inset-0 z-nb-scrim bg-black transition-opacity duration-200 md:hidden ${
                    isMobileNavOpen ? 'opacity-50' : 'pointer-events-none opacity-0'
                }`}
            />

            {/* ── Phone: slide-out drawer ── */}
            <aside
                ref={drawerRef as React.RefObject<HTMLElement>}
                role="dialog"
                aria-modal="true"
                aria-label="Main navigation"
                aria-hidden={!isMobileNavOpen}
                className={`neighbors-sidenav-drawer fixed bottom-0 left-0 top-0 z-nb-drawer flex w-nbp-272 max-w-nb-drawer transform flex-col overflow-y-auto bg-nb-navy text-white shadow-nb-rail transition-all duration-200 ease-out md:hidden ${
                    isMobileNavOpen
                        ? 'visible translate-x-0'
                        : `${isDrawerVisible ? 'visible' : 'invisible'} pointer-events-none -translate-x-full`
                }`}
            >
                <div className="flex flex-shrink-0 items-center gap-nbp-8 border-0 border-b border-solid border-nb-white-12 pl-nbp-16 pr-nbp-8 py-nbp-16">
                    <Link
                        to="/home"
                        onClick={closeMobileNav}
                        className="flex min-w-0 flex-1 items-center justify-center no-underline"
                        aria-label="Speak2 Neighbors home"
                    >
                        <SidenavLogo />
                    </Link>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        onClick={closeMobileNav}
                        aria-label="Close navigation"
                        className="flex h-nbp-44 w-nbp-44 flex-shrink-0 cursor-pointer items-center justify-center rounded-nbp-12 border-0 bg-transparent p-0 text-nb-white-75 transition-all duration-150 hover:bg-nb-white-10 hover:text-white"
                    >
                        <CloseIcon size={22} />
                    </button>
                </div>
                <SidenavLinks items={navItems} currentPath={currentPath} onNavigate={closeMobileNav} />
            </aside>
        </>
    );
};

export default NeighborsSidenav;
